import { set } from 'lodash-es';
import { z } from 'zod';

import { FetchDecorator } from '@tdp/common/fetchDecorator';

import { TokenFetcher } from './abstractions';

export const AuthDecoratorOptionsSchema = z.object({
  // split the comma separated string if it is a string.
  scopes: z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',') : v),
    z.array(z.string()).min(1)
  ),
});

export type AuthDecoratorOptions = z.infer<typeof AuthDecoratorOptionsSchema>;

/**
 * Create a `fetch` decorator that auto-populates the Authorization header with a bearer token
 * for a given
 * @param tokenFetcher the provider for the bearer tokens
 * @param config the options
 */
export function authFetchDecorator(
  tokenFetcher: TokenFetcher,
  config: z.infer<typeof AuthDecoratorOptionsSchema>
): FetchDecorator {
  return function (decorated: typeof fetch): typeof fetch {
    return async function (input, init) {
      const token = await tokenFetcher.getToken(config.scopes);
      if (token.type !== 'success') {
        // TODO(nibeauli): this is almost certainly undesirable behavior but use it for illustration
        // attempt to call with unauthenticated
        return await decorated(input, init);
      }
      // We need to ensure the authorization header is there but that we don't modify the rest of 'init'
      init = set(
        init ?? {},
        ['headers', 'Authorization'],
        `Bearer ${token.token}`
      );
      return await decorated(input, init);
    };
  };
}
